import * as is from 'utilities/is';
import { ACTIONS } from 'constants/annotations';
import { doesPassivelyPause } from 'utilities/annotations';
import { toLegacyQuiz } from 'utilities/analytics';

export class AnnotationsTracker {
    constructor() {
        this.activeIds = [];
        this.knownAnnotations = [];
    }

    getAnnotationUpdateEventResponses(currentAnnotations = []) {
        const changes = this.computeAnnotationChanges(currentAnnotations),
            responses = [
                ...changes.viewStarts.map(id => ({ 
                    action: 'Annotation View',
                    data: this.getDataProperties(this.knownAnnotations.find(a => a.id === id))
                })),
                ...changes.viewEnds.map(id => ({
                    action: 'Annotation Ended',
                    data: this.getDataProperties(this.knownAnnotations.find(a => a.id === id))
                }))
            ];

        return responses;
    }

    getAnnotationActionEventResponses(action, annotation, actionHandlers = []) {
        const responses = [],
            additionalProperties = {};

        const appliedActions = actionHandlers.reduce((applied, { action, args }) => {
            applied[action] = args;
            return applied;
        }, {});
        
        if (appliedActions[ACTIONS.GO_TO_LINK]) {
            additionalProperties['Target'] = appliedActions[ACTIONS.GO_TO_LINK].value;
        }

        if (appliedActions[ACTIONS.GO_TO_TIME]) {
            additionalProperties['Target Time'] = appliedActions[ACTIONS.GO_TO_TIME].value;
        }

        if (appliedActions[ACTIONS.GO_TO_ANNOTATION]) {
            additionalProperties['Target Annotation'] = appliedActions[ACTIONS.GO_TO_ANNOTATION].value;
        }

        if (appliedActions[ACTIONS.SET_VARIABLES]) {
            additionalProperties['Target Expression'] = appliedActions[ACTIONS.SET_VARIABLES].value;
        }

        if (annotation.type === 'pop' && annotation.content) {
            additionalProperties['Text'] = annotation.content;
        }

        if (action === 'Click' && annotation.type === 'contents' && annotation.bookmark) {
            additionalProperties['Target'] = annotation.bookmark.id;
            additionalProperties['Target Time'] = annotation.bookmark.start;
            additionalProperties['Text'] = annotation.bookmark.content;
            additionalProperties['Skipped'] = this.player && Math.abs(+annotation.bookmark.start - this.player.currentTime);

            if (is.adjacentTarget(annotation.target)) {
                additionalProperties['Annotation Type'] = 'external menu';
            }
        }

        if (appliedActions[ACTIONS.DISABLE_SEEK]) {
            action = 'Interaction Restricted';
            additionalProperties['Type'] = 'Interaction Restriction Change';
        }

        if (appliedActions[ACTIONS.ENABLE_SEEK]) {
            action = 'Interaction Allowed';
            additionalProperties['Type'] = 'Interaction Restriction Change';
        }

        if (appliedActions[ACTIONS.EXPAND_TARGET]) {
            additionalProperties['Type'] = 'Expand';
        }

        if (appliedActions[ACTIONS.COLLAPSE_TARGET]) {
            additionalProperties['Type'] = 'Collapse';
        }

        responses.push({
            action,
            data: {
                ...this.getDataProperties(annotation),
                ...additionalProperties 
            }
        });

        return responses;
    }

    computeAnnotationChanges(currentAnnotations) {
        const currentIds = currentAnnotations.map(c => c.id),
            viewStarts = currentIds.filter(id => !this.activeIds.includes(id)),
            viewEnds = this.activeIds.filter(id => !currentIds.includes(id));

        this.activeIds = currentIds;
        this.knownAnnotations = this.knownAnnotations.concat(currentAnnotations.filter(a => viewStarts.includes(a.id)));

        return {
            viewStarts,
            viewEnds
        }
    }

    getDataProperties(annotation) {
        const duration = annotation.end - annotation.start,
            dataProperties = {
                'Type': annotation.type,
                'Annotation UUID': annotation.id,
                'Annotation Type': annotation.type,
                'Annotation Subtype': annotation.toolType,
                'Annotation Start Time': annotation.start || 0,
                'Gating': annotation.gated || false,
                'Pausing': doesPassivelyPause(annotation.actions)
            };

        if (annotation.type === 'iframe') {
            dataProperties['Extension Source'] = annotation.content;
        }

        if (is.object(annotation.internal) && is.defined(annotation.internal.refId)) {
            dataProperties['Annotation Ref ID'] = annotation.internal.refId;
        }

        if (annotation.customEventData) {
            dataProperties['Custom Event Data'] = annotation.customEventData;
        }

        if (annotation.formData) {
            dataProperties['Quiz'] = toLegacyQuiz(annotation.formData);
        }
        
        if (this.player && this.player.duration) {
            dataProperties['Remaining'] = this.player.duration - this.player.currentTime;
        }

        if (isNaN(duration)) {
            if (this.player && this.player.duration) {
                if (is.defined(annotation.start)) {
                    dataProperties['Annotation Duration'] = this.player.duration - annotation.start;
                } else {
                    dataProperties['Annotation Duration'] = this.player.duration;
                }
                
            }
        } else {
            dataProperties['Annotation Duration'] = duration;
        }

        return dataProperties;
    }
}